// SourceDetailV3Page — the new default landing for /sources/:id (2026-05-09).
//
// UX design (10/10 spec, see commit message for full backlog):
//
// Scenarios covered as primary flows:
//   S1  Empty source            → SendDataPanel (open by default if 0 events)
//                                  shows webhook URL + drop zone + curl
//   S2  Watching events arrive  → Auto-refresh every 10s + visual pulse on
//                                  new top row + "live" header pill
//   S3  Drop CSV anywhere       → Window-level drag-drop overlay routes to
//                                  the source's bulk upload endpoint
//   S4  Click cell to filter    → Cell click pins value as `eq` filter chip
//   S5  Save as view + forward  → Inline recipe + ingest-action picker
//                                  creates BQ view + trigger in one action
//   S6  Different shapes        → Schema sidebar lists payload fields with
//                                  type + frequency; click toggles
//                                  is_present filter
//   S7  Errors                  → Header errors badge surfaces failed runs
//                                  count (link to V2 errors tab for detail)
//   S8  Lineage                 → (deferred to next iteration — V2 lineage
//                                  drawer accessible via row click in V2)
//   S9  Single page             → No sub-tabs, no modal-first interactions

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Source, Trigger } from '@datahub/shared';
import {
  getSource,
  listBqViews,
  listTriggers,
  listSourceErrors,
  getEventsPreview,
  getEventsCount,
  type BqView,
} from '../api';
import TopHeader from '../components/TopHeader';
import { useToast } from '../components/Toast';
import { ReplayDialog } from '../components/dialogs/ReplayDialog';
import { listViews } from '../api';
import type { View as ViewType } from '@datahub/shared';
import { SourceHeaderV3 } from '../components/v3/SourceHeaderV3';
import { SendDataPanelV3 } from '../components/v3/SendDataPanelV3';
import { DataSurfaceV3 } from '../components/v3/DataSurfaceV3';
import { TriggersBarV3 } from '../components/v3/TriggersBarV3';
import { EmptyStateHeroV3 } from '../components/v3/EmptyStateHeroV3';
import { ErrorsPanelV3 } from '../components/v3/ErrorsPanelV3';
import { FilesPanelV3 } from '../components/v3/FilesPanelV3';
import { DetectedModelsPanelV3 } from '../components/v3/DetectedModelsPanelV3';
import { DailyImportNudgeV3 } from '../components/v3/DailyImportNudgeV3';
import type { TriggerRun } from '@datahub/shared';

export default function SourceDetailV3Page(): React.ReactElement | null {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [source, setSource] = useState<Source | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'not_found' | 'error' | 'ready'>('loading');

  const [views, setViews] = useState<BqView[]>([]);
  const [filterTreeViews, setFilterTreeViews] = useState<ViewType[]>([]);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [errors, setErrors] = useState<TriggerRun[]>([]);
  const [eventCount, setEventCount] = useState<number | null>(null);
  const [errorCount, setErrorCount] = useState(0);
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);

  const [dragOver, setDragOver] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [dataReloadKey, setDataReloadKey] = useState(0);
  const [showReplay, setShowReplay] = useState(false);

  // Load source on mount.
  useEffect(() => {
    if (!id) return;
    setLoadState('loading');
    getSource(id)
      .then(s => { setSource(s); setLoadState('ready'); })
      .catch(err => {
        if (String(err).includes('404')) setLoadState('not_found');
        else { setLoadState('error'); toast.show(`Load failed: ${(err as Error).message}`, { kind: 'error' }); }
      });
  }, [id, toast]);

  // Side-effects loader: views + triggers + errors + event preview.
  // Re-runs on every dataReloadKey bump (test send, drop, etc.).
  useEffect(() => {
    if (!id || loadState !== 'ready') return;
    let alive = true;
    void (async () => {
      const [v, ftv, t, e, p] = await Promise.allSettled([
        listBqViews(id),
        listViews(id),
        listTriggers(id),
        listSourceErrors(id, 50),
        getEventsPreview(id, 1),
      ]);
      if (!alive) return;
      if (v.status === 'fulfilled') setViews(v.value.views);
      if (ftv.status === 'fulfilled') setFilterTreeViews(ftv.value);
      if (t.status === 'fulfilled') setTriggers(t.value);
      if (e.status === 'fulfilled') {
        setErrors(e.value);
        setErrorCount(e.value.length);
      }
      if (p.status === 'fulfilled') {
        setLastEventAt(p.value[0]?.received_at ?? null);
      }
    })();
    return () => { alive = false; };
  }, [id, loadState, dataReloadKey]);

  // Periodic refresh of header stats (event count + last event timestamp).
  // Uses the new /events:count BQ-backed endpoint (audit fix from
  // operator report '200 events stuck in header'). Falls back to
  // getEventsPreview for sources whose dataset hasn't been provisioned
  // yet (returns total=0).
  //
  // Cost audit #1 (P0): this previously polled every 15s
  // unconditionally — each tick fires 3 COUNTIF aggregates against
  // the T2 external table (scans GCS payload JSON). N tabs × M
  // sources × 240 polls/hr added up fast. Now: 60s default + pause
  // on hidden tab. Operator who wants live throughput uses the
  // DataSurfaceV3 Refresh-options dropdown.
  useEffect(() => {
    if (!id || loadState !== 'ready') return;
    const tick = (): void => {
      if (document.hidden) return;
      void getEventsCount(id).then(r => {
        // Show last_24h as the 'live' header count — operator's
        // typical mental model is 'events flowing right now'.
        // Fall back to total when 24h is empty but all-time has data.
        setEventCount(r.last_24h > 0 ? r.last_24h : r.total);
        setLastEventAt(r.last_event_at);
      }).catch(() => {
        // Best-effort fallback to the Firestore preview path.
        void getEventsPreview(id, 1).then(events => {
          if (events.length > 0) setLastEventAt(events[0]!.received_at);
        }).catch(() => undefined);
      });
    };
    tick();
    const h = setInterval(tick, 60_000);
    function onVisibility(): void { if (!document.hidden) tick(); }
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(h); document.removeEventListener('visibilitychange', onVisibility); };
  }, [id, loadState, dataReloadKey]);

  // Window-level drag-and-drop for CSV/JSON/XLSX. Drop anywhere on the
  // page → routes to /upload?bulk=true on the source. Visible overlay
  // appears on dragover so the operator knows the drop is being captured.
  useEffect(() => {
    if (!id) return;
    function onDragOver(e: DragEvent): void {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      setDragOver(true);
    }
    function onDragLeave(e: DragEvent): void {
      if (e.relatedTarget === null || (e.target as Element)?.tagName === 'HTML') {
        setDragOver(false);
      }
    }
    async function onDrop(e: DragEvent): Promise<void> {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      setDropping(true);
      try {
        const r = await fetch(`/upload/${id}?bulk=true&filename=${encodeURIComponent(file.name)}`, {
          method: 'POST',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (r.ok || r.status === 202) {
          toast.show(`Uploaded ${file.name} — refreshing data`, { kind: 'success' });
          setDataReloadKey(k => k + 1);
        } else {
          const body = await r.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
          toast.show(`Upload failed: ${body.error?.detail ?? r.status}`, { kind: 'error' });
        }
      } catch (err) {
        toast.show(`Upload failed: ${(err as Error).message}`, { kind: 'error' });
      } finally {
        setDropping(false);
      }
    }
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [id, toast]);

  if (!id) return null;
  if (loadState === 'loading') {
    return (
      <>
        <TopHeader breadcrumb={[{ label: 'Sources', to: '/sources' }]} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>
      </>
    );
  }
  if (loadState === 'not_found') {
    return (
      <>
        <TopHeader breadcrumb={[{ label: 'Sources', to: '/sources' }]} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Source not found.</div>
      </>
    );
  }
  if (loadState === 'error' || !source) {
    return (
      <>
        <TopHeader breadcrumb={[{ label: 'Sources', to: '/sources' }]} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}>Failed to load source.</div>
      </>
    );
  }

  const dataset = source.bq_dataset ?? '';

  return (
    <>
      <TopHeader breadcrumb={[{ label: 'Sources', to: '/sources' }]} />
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 24px 80px' }}>
        <SourceHeaderV3
          source={source}
          eventCount={eventCount}
          errorCount={errorCount}
          lastEventAt={lastEventAt}
          onSourceChange={setSource}
          onOpenReplay={() => setShowReplay(true)}
        />

        <ErrorsPanelV3
          errors={errors}
          triggers={triggers}
          sourceId={source.source_id}
          onChanged={() => setDataReloadKey(k => k + 1)}
        />

        {/* First-event hero: replaces the Send-data + Data surface
            until the source has any events. The hero IS the empty
            state; once the first event lands, the regular layout
            kicks in. */}
        {eventCount !== null && eventCount === 0 ? (
          <EmptyStateHeroV3
            source={source}
            onTestSent={() => setDataReloadKey(k => k + 1)}
          />
        ) : (
          <>
            {/* Detected models takes the prime spot below the header
                — it's the highest-value thing the operator can do with
                a populated source ('turn this stream into BQ tables').
                Send-data + files panels live further down: once events
                are flowing they're admin tasks, not the main flow. */}
            {dataset && (
              <DailyImportNudgeV3
                sourceId={source.source_id}
                dataset={dataset}
                reloadKey={dataReloadKey}
                onTriggerCreated={() => setDataReloadKey(k => k + 1)}
              />
            )}

            {dataset && (
              <DetectedModelsPanelV3
                sourceId={source.source_id}
                dataset={dataset}
                views={views}
                reloadKey={dataReloadKey}
                onModelCreated={() => setDataReloadKey(k => k + 1)}
              />
            )}

            {dataset && (
              <DataSurfaceV3
                sourceId={source.source_id}
                source={source}
                dataset={dataset}
                views={views}
                triggers={triggers}
                onViewSaved={() => setDataReloadKey(k => k + 1)}
              />
            )}

            {/* Automations live next to the data they act on. Operator
                builds a filter in the table above → saves as view →
                picks an "Also apply at ingest" action; the trigger
                lands here. Pause / archive controls are 1 click from
                the data view, no scroll required. */}
            <TriggersBarV3
              triggers={triggers}
              sourceId={source.source_id}
              onTriggersChanged={() => setDataReloadKey(k => k + 1)}
            />

            {/* "How to send more data" demoted to the bottom group —
                operator's primary loop is explore + automate (above);
                the URL + curl + Browse-file button live here for the
                moment they need to copy something into a new tool or
                debug a partner integration. */}
            <SendDataPanelV3
              source={source}
              onTestSent={() => setDataReloadKey(k => k + 1)}
              onSourceChange={() => {
                // Re-fetch the source doc so the hooks list reflects
                // create/rename/tag-edit/archive without a page
                // reload. Bump dataReloadKey too so views/triggers
                // refresh in case the operator's change knocks
                // anything else.
                if (id) {
                  void getSource(id).then(s => setSource(s)).catch(() => undefined);
                }
                setDataReloadKey(k => k + 1);
              }}
            />

            {/* Audit / past file uploads — last in the stack so an
                operator who's never uploaded a file doesn't see an
                empty box dominating the page. */}
            <FilesPanelV3
              sourceId={source.source_id}
              reloadKey={dataReloadKey}
              onReprocessed={() => setDataReloadKey(k => k + 1)}
            />
          </>
        )}
      </div>

      {/* Replay dialog — re-fire triggers on a date range. Reused from V2;
          views + triggers passed so the operator can scope replay to a
          saved FilterTree view or a specific trigger version. */}
      {showReplay && (
        <ReplayDialog
          sourceId={source.source_id}
          views={filterTreeViews}
          triggers={triggers}
          onCancel={() => setShowReplay(false)}
          onLaunched={() => { setShowReplay(false); setDataReloadKey(k => k + 1); }}
        />
      )}

      {/* Drag-and-drop overlay — visible while operator drags a file
          over the window. Drop anywhere routes to bulk upload on the
          source's slug. */}
      {(dragOver || dropping) && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'oklch(0.96 0.10 145 / 0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            background: 'var(--bg-elev)',
            border: '2px dashed oklch(0.62 0.18 145)',
            padding: '28px 36px', borderRadius: 12,
            textAlign: 'center',
            fontSize: 18, fontWeight: 500, color: 'oklch(0.32 0.16 145)',
          }}>
            {dropping ? 'Uploading…' : `Drop a file to ingest into ${source.name}`}
            <div style={{ marginTop: 6, fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--ink-3)', fontWeight: 400 }}>
              CSV · JSON · NDJSON · XLSX · XML · TSV
            </div>
          </div>
        </div>
      )}
    </>
  );
}
